import React, { useEffect } from 'react';
import { CloseButton } from '../../atoms/CloseButton';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  showCloseButton?: boolean;
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'medium',
  showCloseButton = true,
}: ModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    large: 'max-w-4xl',
    xlarge: 'max-w-6xl',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Backdrop with better blur */}
      <div
        className="fixed inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70 backdrop-blur-md transition-opacity animate-[fadeIn_0.2s_ease-out]"
      />

      {/* Modal with enhanced styling */}
      <div
        className={`relative bg-white rounded-3xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col transform transition-all duration-300 border border-gray-100/50`}
        onClick={(e) => e.stopPropagation()}
        style={{
          animation: 'fadeInScale 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Enhanced Header with gradient */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-slate-50 via-white to-slate-50 rounded-t-3xl">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
            {title}
          </h2>
          {showCloseButton && <CloseButton onClick={onClose} />}
        </div>

        {/* Content with custom scrollbar */}
        <div className="flex-1 overflow-y-auto px-8 py-6 modal-scrollbar">
          {children}
        </div>

        {/* Enhanced Footer */}
        {footer && (
          <div className="px-8 py-5 border-t border-gray-100 bg-gradient-to-b from-gray-50/50 to-white rounded-b-3xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
